// CONTRACT:
// PURPOSE: [Hero stat bar с counter-up анимацией (4 карточки статистики на странице рейтинга ЧОП)]
// SCOPE: [rating hero, stat-bar, counter-up, intersection-observer]
// INPUT: [totalAll: number, totalRegions: number, totalReviews: number]
// OUTPUT: [4 stat cards с animated counter-up числами]
// KEYWORDS: [DOMAIN(SEO): hero-stats; CONCEPT(animation): counter-up, requestAnimationFrame; UI: grid, stat-bar]

"use client";

import { useEffect, useRef, useState } from "react";

// START_FUNCTION_pluralReviews: русский плюрализатор «отзыв/отзыва/отзывов»
// MEMENTO: 1/11/21/101 → отзыв; 2-4/22-24 → отзыва; 0/5-20 → отзывов
function pluralReviews(n: number): string {
  const mod100 = Math.abs(n) % 100;
  if (mod100 >= 11 && mod100 <= 14) return "отзывов";
  const mod10 = mod100 % 10;
  if (mod10 === 1) return "отзыв";
  if (mod10 >= 2 && mod10 <= 4) return "отзыва";
  return "отзывов";
}
// END_FUNCTION_pluralReviews

// # START_INTERFACE_RatingStatsBarProps
interface RatingStatsBarProps {
  totalAll: number;
  totalRegions: number;
  totalReviews: number;
}
// # END_INTERFACE_RatingStatsBarProps

// # START_FUNCTION_useCountUp
/**
 * Хук для counter-up анимации.
 * Использует IntersectionObserver (threshold 0.6) для запуска,
 * requestAnimationFrame с ease-out cubic: e = 1 - Math.pow(1 - p, 3).
 * При prefers-reduced-motion — сразу финальное значение.
 */
function useCountUp(target: number, durationMs = 1600) {
  const [value, setValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // prefers-reduced-motion check
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setValue(target);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = performance.now();

          const animate = (now: number) => {
            const progress = Math.min((now - startTime) / durationMs, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.floor(eased * target));
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setValue(target);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.6 }
    );

    observer.observe(el);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return { value, ref };
}
// # END_FUNCTION_useCountUp

// # START_FUNCTION_StatCard
/**
 * Одна карточка статистики с counter-up числом.
 * Внутри использует useCountUp для анимации.
 */
function StatCard({
  value,
  suffix,
  prefix,
  label,
  isGreenSuffix,
}: {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  isGreenSuffix?: boolean;
}) {
  const { value: animated, ref } = useCountUp(value);

  return (
    <div className="p-5 md:p-6 hover:bg-[hsl(var(--portal-surface))] transition-colors">
      <div className="font-mono text-3xl lg:text-4xl font-black mb-2 flex items-baseline gap-0.5">
        {prefix && (
          <span className={`${isGreenSuffix ? "text-[hsl(var(--portal-accent))]" : "text-[hsl(var(--portal-text))]"}`}>
            {prefix}
          </span>
        )}
        <span ref={ref} className="text-[hsl(var(--portal-text))]">
          {animated.toLocaleString("ru-RU")}
        </span>
        {suffix && (
          <span className={`${isGreenSuffix ? "text-[hsl(var(--portal-accent))]" : "text-[hsl(var(--portal-text))]"} ml-0.5`}>
            {suffix}
          </span>
        )}
      </div>
      <div className="text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest font-mono mt-1.5">
        {label}
      </div>
    </div>
  );
}
// # END_FUNCTION_StatCard

// # START_FUNCTION_RatingStatsBar
/**
 * Hero stat bar с 4 карточками статистики и counter-up анимацией.
 * Desktop: 4 колонки, Mobile: 2 колонки.
 */
export function RatingStatsBar({ totalAll, totalRegions, totalReviews }: RatingStatsBarProps) {
  // 4-я карточка — статичная "24ч Обновление данных", но тоже анимируем 24
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[hsl(var(--portal-border-hover))] border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))]">
      {/* 1. ЧОП в реестре */}
      <StatCard
        value={totalAll}
        suffix="+"
        label="ЧОП в реестре"
        isGreenSuffix
      />

      {/* 2. Регионов */}
      <StatCard
        value={totalRegions}
        label="Регионов"
      />

      {/* 3. Отзывов собрано */}
      <StatCard
        value={totalReviews}
        label={`${pluralReviews(totalReviews)} собрано`}
      />

      {/* 4. Обновление данных */}
      <StatCard
        value={24}
        suffix="ч"
        prefix="●"
        label="Обновление данных"
        isGreenSuffix
      />
    </div>
  );
}
// # END_FUNCTION_RatingStatsBar
